/**
 * CSV header utilities — the SINGLE isomorphic source for making CSV header
 * names unique, shared by the backend parse/preview AND the frontend custom
 * importer's column dropdown.
 *
 * A parsed row is a `Record<string, unknown>` keyed by header name, so two
 * columns sharing a name (Huobi exports two `Tipo` columns — operation category
 * AND buy/sell side; Bitrue trade exports two `Currency` columns — received AND
 * sent asset) would COLLAPSE to one value (last-wins), silently losing a column.
 *
 * Suffixing repeats with `__N` (positional order preserved) keeps every column
 * addressable: the first `Tipo`/`Currency` stays as-is, the second becomes
 * `Tipo__2`/`Currency__2`. The backend already deduped on its parse + preview;
 * the frontend column dropdown did NOT, so a user could not map the second
 * same-named column (the duplicate option resolved to the first) — this is the
 * one implementation both sides now use so the dropdown and the engine agree.
 */
export function dedupeCsvHeaders(rawHeaders: string[]): string[] {
  const seen = new Map<string, number>()
  const result: string[] = []
  for (const raw of rawHeaders) {
    const count = seen.get(raw) ?? 0
    result.push(count === 0 ? raw : `${raw}__${count + 1}`)
    seen.set(raw, count + 1)
  }
  return result
}

/** Count how many headers are duplicates (for diagnostics/logging). */
export function countDuplicateHeaders(rawHeaders: string[]): number {
  return rawHeaders.length - new Set(rawHeaders).size
}
